import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  Input,
  NgZone,
  OnInit,
  Inject,
  Component,
  ViewChild,
  OnDestroy,
  ElementRef,
  ChangeDetectionStrategy,
  HostBinding,
  ChangeDetectorRef,
} from '@angular/core';

import { SnDVRStream, SnLiveVideoUrls } from '@lib/api/entities';
import { SnSessionService } from '@lib/auth';
import { COLORS } from '@lib/ui/theme';

import { PtzControlsService } from '../../ptz-controls.service';
import { SnVideoPlayerComponent } from '../../video-player.component';

@Component({
  selector: 'sn-video-player-progress',
  templateUrl: './video-player-progress.control.html',
  styleUrls: ['./video-player-progress.control.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sn-video-player-progress',
  },
})
export class SnVideoPlayerProgressControl implements OnInit, OnDestroy {
  @HostBinding('class.seeThrough')
  get isAreaZoomDrawing(): boolean {
    return this.isDrawing;
  }

  // 12 hours or less
  get loopDuration(): number {
    return Math.min(this._player.loopDuration * 60, 43200);
  }

  private get _currentTime(): number {
    return +this._currentSliderInputElementRef.nativeElement.value;
  }

  public isDrawing: boolean;

  @HostBinding('class.paused')
  public paused: boolean;

  @Input()
  public prerecordingUrls: SnDVRStream;

  @Input()
  public liveUrls: any | SnLiveVideoUrls;

  public showThumb: boolean;

  public isFullScreen = false;

  private readonly _currentSliderInputEventListeners;

  private readonly _destroy$ = new Subject<void>();

  private _lastCurrentTime: number;

  @ViewChild('playbackLoop', { static: true })
  private readonly _playbackLoopElementRef: ElementRef<HTMLElement>;

  @ViewChild('thumb', { static: true })
  private _thumbElementRef: ElementRef;

  @ViewChild('currentSliderInput', { static: true })
  private readonly _currentSliderInputElementRef: ElementRef<HTMLInputElement>;
  private _thumbnailTimeout;

  constructor(
    @Inject('SN_PLAYER') private readonly _player: SnVideoPlayerComponent,
    private readonly _cd: ChangeDetectorRef,
    private readonly _ngZone: NgZone,
    private readonly _ptzService: PtzControlsService,
    private readonly _sessionService: SnSessionService,
  ) {
    this._currentSliderInputEventListeners = new Map<string, EventListenerOrEventListenerObject>();
  }

  public ngOnInit(): void {
    this._player.fullscreen$.pipe(takeUntil(this._destroy$)).subscribe((fullscreen) => {
      this.isFullScreen = fullscreen;
      this._cd.detectChanges();
    });

    this._ptzService.ptzMovement$
      .pipe(takeUntil(this._destroy$))
      .subscribe((isDrawing) => (this.isDrawing = isDrawing));
    this._update();
    this._ngZone.runOutsideAngular(() => this._initSubscriptions());
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._currentSliderInputEventListeners.forEach((listener, event) => {
      this._currentSliderInputElementRef.nativeElement.removeEventListener(event, listener);
    });
  }

  private _initSubscriptions(): void {
    this._player.live$
      .pipe(
        filter((live) => live),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._currentSliderInputElementRef.nativeElement.value = '0';
        this._update();
        this._updatePlaybackLoop();
      });

    this._player.currentTime$.pipe(takeUntil(this._destroy$)).subscribe((time) => {
      if (!this._player.live) {
        if (this._lastCurrentTime) {
          const diff = time - this._lastCurrentTime;
          const value = this._currentSliderInputElementRef.nativeElement.value;
          this._currentSliderInputElementRef.nativeElement.value = Math.floor(
            +value + diff,
          ).toString();
          this._update();
        }
        this._lastCurrentTime = time;
      }
      this._updatePlaybackLoop();
    });

    this._player.state$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this.paused = state === 'pause';
    });

    this._currentSliderInputEventListeners.set('change', this._sliderValueChangeHandler.bind(this));
    this._currentSliderInputEventListeners.set('input', this._sliderInputHandler.bind(this));

    this._currentSliderInputEventListeners.forEach((listener, event) => {
      this._currentSliderInputElementRef.nativeElement.addEventListener(event, listener);
    });
  }

  private _sliderInputHandler(): void {
    this.showThumb = true;
    this._cd.detectChanges();
    this._player.pause();
    this._updatePlaybackLoop();
    this._handleThumbnail();
  }

  private _update(): void {
    const currentSliderWidth = ((this.loopDuration + this._currentTime) * 100) / this.loopDuration;
    const currentSliderInputElement = this._currentSliderInputElementRef.nativeElement;

    currentSliderInputElement.value = this._currentTime.toString();
    currentSliderInputElement.style.backgroundImage = `linear-gradient(
      to right,
      ${COLORS['accent-600']} 0%,
      ${COLORS['accent-600']} ${currentSliderWidth}%,
      transparent ${currentSliderWidth}%,
      transparent 100%
    )`;
  }

  private _updatePlaybackLoop(): void {
    const now = Date.now();
    const date = new Date(now - Math.abs(this._currentTime) * 1000);
    const left = ((this.loopDuration + this._currentTime) * 100) / this.loopDuration;
    this._player.timeStamp = date;

    this._playbackLoopElementRef.nativeElement.innerHTML = date
      ? `${date.toLocaleTimeString()}`
      : null;
    this._playbackLoopElementRef.nativeElement.style.left = `${left}%`;
    this._thumbElementRef.nativeElement.style.left = `${left}%`;

    if (left > 33 && left < 66) {
      this._playbackLoopElementRef.nativeElement.classList.add('center');
      this._playbackLoopElementRef.nativeElement.classList.remove('right');
      this._thumbElementRef.nativeElement.classList.add('center');
      this._thumbElementRef.nativeElement.classList.remove('right');
      this._thumbElementRef.nativeElement.classList.remove('move-l');
    } else if (left > 66) {
      this._playbackLoopElementRef.nativeElement.classList.add('right');
      this._playbackLoopElementRef.nativeElement.classList.remove('center');
      this._thumbElementRef.nativeElement.classList.add('right');
      this._thumbElementRef.nativeElement.classList.remove('center');
      this._thumbElementRef.nativeElement.classList.remove('move-l');
    } else {
      this._playbackLoopElementRef.nativeElement.classList.remove('right');
      this._playbackLoopElementRef.nativeElement.classList.remove('center');
      this._thumbElementRef.nativeElement.classList.remove('right');
      this._thumbElementRef.nativeElement.classList.remove('center');
      this._thumbElementRef.nativeElement.classList.add('move-l');
    }
  }

  private _sliderValueChangeHandler(): void {
    if (this._currentTime) {
      this._player.isFmp4 = !!this.prerecordingUrls?.timeshiftFmp4;
      this._player.live = false;
      switch (true) {
        case this._sessionService.isFederal:
          const now = Date.now();
          const startDate = new Date(now - Math.abs(this._currentTime * 1000));
          const startInSeconds = Math.floor(startDate.getTime() / 1000);
          const nowInSeconds = Math.floor(now / 1000);
          const src = this.prerecordingUrls.video
            .replace('<start_timestamp>', startInSeconds.toString())
            .replace('<end_timestamp>', nowInSeconds.toString());
          this._player.src = src;
          break;
        case this._player.isFmp4:
          this._player.src = this.prerecordingUrls.toStreamSourcesFmp4(this._currentTime);
          break;
        default:
          this._lastCurrentTime = null;
          this._player.src = this.prerecordingUrls.toStreamSources(this._currentTime);
          break;
      }
    } else {
      if (!this._player.live) {
        this._player.src = this.liveUrls;
        this._player.live = true;
      }
    }
    this.showThumb = false;
    this._update();
    this._cd.detectChanges();
  }

  private _handleThumbnail(): void {
    if (this._player.isFmp4) {
      return;
    }
    clearTimeout(this._thumbnailTimeout);
    const now = Date.now();

    const date = Math.floor(new Date(now - Math.abs(this._currentTime * 1000)).getTime() / 1000);
    this._thumbnailTimeout = setTimeout(() => {
      if (!this._thumbElementRef.nativeElement) {
        return;
      }
      this._thumbElementRef.nativeElement.src = this.prerecordingUrls?.getMp4Thumbnails(date);
    }, 300);
  }
}
